import moment, { ISO_8601, Moment } from "moment";
import Parameter from "./parameter";
import ModuleCardList from "./module-card-list";
import Module from "./module";
import FieldPromote from "./field-promote";
import Field from "./field";
import Entity from "./entity";
import Api3, { Response } from "./api3";
import Api from "./api";
import { IProviderDataRequest, IProviderDataResponse } from "../interface/provider-data";
import IModule from "../interface/module";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import IDataConnection, {
    IConnectionEntity, IConnectionField, IConnectionRelation
} from "../interface/data-connection";
import IApi3SchemaTable, { ISchemaField, ISchemaForeignKey } from "../interface/api3-schema";
import Global from "../global";
import ParamStorage from "../enum/param-storage";
import LogicalValue from "../enum/logical-value";
import FieldType from "../enum/field-type";
import FieldSystemType from "../enum/field-system-type";
import DateType from "../enum/date-type";
import DefaultConfig from "../default-config";
import ParameterStorage from "../class/parameter-storage";

// todo - create class for connection to not to pass .log and connection to every function???

class DataConnectionSingleton {

    /* Public Methods */

    public getAll(): IDataConnection[] {
        return this.getStore().getAll();
    }

    public async delete(id: number, logCallback?: (msg: string) => void): Promise<number> {
        const log = (msg: string) => logCallback ? logCallback(msg) : null;
        const connection: IDataConnection | null = this.getStore().get(id);
        if (connection) {
            log(`disconnecting "${connection.name}"...`);
            await this.getStore().delete(id);
            const entities: IEntity[] = Entity.getAll().filter((e: IEntity) => {
                return e.connection && e.connection.connectionId === connection.uid;
            });
            for (const entity of entities) {
                log(`deleting "${entity.singular}" data...`);
                await Entity.delete(entity.id);
                log(`entity "${entity.singular}" deleted`);
            }
            const module: IModule[] = Module.getAll().filter((m: IModule) => {
                return m.name === connection.name && m.cards && m.cards.length === 0;
            });
            if (module.length === 1) {
                log(`deleting module "${module[0].name}" data...`);
                await Module.delete(module[0].id as number);
                log(`module "${module[0].name}" deleted`);
            }
            log(`"${connection.name}" is now disconnected`);
        } else {
            log(`error: connection not found`);
        }
        return id;
    }

    public async esteblish(data: IDataConnection, address: string = '', logCallback?: (msg: string) => void): Promise<IDataConnection | null> {
        const log = (msg: string) => logCallback ? logCallback(msg) : null;
        if (address) {
            Api3.setInstance({ name: 'custom', address, hostnames: ['*', 'localhost', 'localhost:3006', 'main.frametest.eu'] });
        }
        /* Request to api3 to connect and to get tables and fields */
        log(`connecting to database...`);
        const dataConnection: IDataConnection | null = await this.syncSchema(data, logCallback);
        if (dataConnection) {
            await this.syncData(dataConnection, logCallback);
            await this.syncRelations(dataConnection, logCallback);
            log(`database connection created`);
            return dataConnection;
        } else {
            log(`error: cannot connect to database`);
            return null;
        }
    }

    public async syncSchema(connection: IDataConnection, logCallback?: (msg: string) => void): Promise<IDataConnection | null> {
        const log = (msg: string) => logCallback ? logCallback(msg) : null;
        const response: Response<IApi3SchemaTable[]> | null = await Api3.connectionGetSchema(connection);
        /* If success - Create dummies and save mapping to param storage */
        if (response) {
            log(`connection esteblished`);
            const module: IModule = await Module.create({
                name: connection.name,
                icon: Global.DEFAULT_CONNECTION_MODULE_ICON,
                layout: []
            });
            connection.uid = this.getUid(connection.name);
            const mapping: IConnectionEntity[] = [];
            for (const dbTable of response.data) {
                log(`mapping "${dbTable.name}"`);
                const fields: Partial<IField>[] = [];
                for (const dbField of dbTable.fields) {
                    const field: Partial<IField> = {
                        singular: dbField.name,
                        uiType: dbField.type
                    };
                    // todo - add proper field config (e.g. decimal and int)
                    if (dbField.type === FieldType.number) {
                        field.uiConfig = DefaultConfig[FieldType.number]();
                    } else if (dbField.type === FieldType.datetime) {
                        field.uiConfig = DefaultConfig[FieldType.datetime](DateType.DateTime);
                    }
                    field.systemType = Field.getSystemType(field.uiType as FieldType);
                    fields.push(field);
                }
                const entity: IEntity = await Entity.create(dbTable.shortName, fields);
                const connectionEntity: IConnectionEntity = {
                    id: entity.id,
                    connectionId: connection.uid,
                    table: dbTable.name,
                    primaryKey: null,
                    foreignKeys: [],
                    fields: []
                }
                for (const field of entity.fields) {
                    const dbField: ISchemaField[] = dbTable.fields.filter(dbf => dbf.name === field.singular);
                    if (dbField.length === 1) {
                        const connectionField: IConnectionField = {
                            id: field.id,
                            connectionId: connection.uid,
                            table: dbTable.name,
                            field: dbField[0].name,
                            primaryKey: dbTable.primaryKey === dbField[0].name,
                            foreignKey: null
                        }
                        if (connectionField.primaryKey) {
                            connectionEntity.primaryKey = field.id;
                        }
                        const key: ISchemaForeignKey | undefined = dbTable.foreignKeys.find(f => f.field === dbField[0].name);
                        if (key) {
                            connectionField.foreignKey = {
                                id: field.id,
                                connectionId: connection.uid,
                                name: key.name,
                                table: dbTable.name,
                                field: dbField[0].name,
                                foreignTable: `${key.foreignTable}`,
                                foreignField: key.foreignField
                            };
                            connectionEntity.foreignKeys.push(connectionField.foreignKey);
                        }
                        connectionEntity.fields.push(connectionField);
                        log(`created field "${dbField[0].name}" ("${dbTable.name}"): ${field.id}`);
                        await Field.setConnection(field.id, connectionField);
                    } else {
                        log(`error: cannot map field "${field.singular}" of entity "${entity.singular}`);
                    }
                }
                log(`created entity "${dbTable.name}": ${entity.id}`);
                mapping.push(connectionEntity);
                await Entity.setConnection(entity.id, connectionEntity);
                await ModuleCardList.createByEntityId(entity.id, module.id);
                log(`"${dbTable.name}" mapping finished`);
            }
            connection.mapping = mapping;
            log(`schema transfered to FrameLink`);
            return await this.getStore().create(connection);
        }
        return null;
    }

    public async syncRelations(connection: IDataConnection, logCallback?: (msg: string) => void): Promise<IDataConnection | null> {
        const log = (msg: string) => logCallback ? logCallback(msg) : null;
        log(`creating relations...`);
        for (const dbTable of connection.mapping) {
            for (const foreignKey of dbTable.foreignKeys) {
                await this.createRelation(connection, foreignKey, logCallback);
            }
        }
        /* updating mapping to include new fields ids */
        await this.getStore().update(connection);
        return connection;
    }

    public async syncData(connection: IDataConnection, logCallback?: (msg: string) => void): Promise<IProviderDataResponse[]> {
        const log = (msg: string) => logCallback ? logCallback(msg) : null;
        const request: IProviderDataRequest[] = [];
        for (const dbTable of connection.mapping) {
            request.push({
                table: dbTable.table,
                fields: dbTable.fields.map(f => f.field),
                limit: 5000
            });
        }
        log(`sync data`);
        log(`retreiving data from server...`);
        const response: Response<IProviderDataResponse[]> | null = await Api3.connectionGetData({ ...connection, request });
        if (response) {
            log(`data received from server: ${response.data.length} tables`);
            for (const mapTable of connection.mapping) {
                log(`mapping "${mapTable.table}" data...`);
                const resp: IProviderDataResponse | undefined = response.data.find(r => r.table === mapTable.table);
                const fieldTypes: { [id: number]: FieldType } = {};
                for (const mapField of mapTable.fields) {
                    const field: IField | null = Field.get(mapField.id);
                    if (field) {
                        fieldTypes[mapField.id] = field.uiType;
                    } else {
                        fieldTypes[mapField.id] = FieldType.text;
                    }
                }
                if (resp && resp.data.length) {
                    const docs: IDocument[] = [];
                    for (const record of resp.data) {
                        const doc: IDocument = {};
                        for (const mapField of mapTable.fields) {
                            const value: any = (record as any)[mapField.field];
                            if (value !== undefined) {
                                let docValue: IDocumentValue = '';
                                switch (fieldTypes[mapField.id]) {
                                    case FieldType.number:
                                        docValue = null;
                                        if (typeof value === 'number' && !isNaN(value)) {
                                            docValue = value as number;
                                        } else if (typeof value === 'string') {
                                            const numValue: number = Number(value);
                                            if (!isNaN(numValue)) {
                                                docValue = numValue;
                                            }
                                        }
                                        break;
                                    case FieldType.datetime:
                                        if (typeof value === 'string') {
                                            const m: Moment = moment(value, ISO_8601);
                                            if (m.isValid()) {
                                                docValue = value;
                                            }
                                        }
                                        break;
                                    case FieldType.check:
                                        if (typeof value === 'boolean') {
                                            docValue = value ? LogicalValue.True : LogicalValue.False;
                                        } else if (typeof value === 'string') {
                                            if (value === 'true' || value === '1') {
                                                docValue = LogicalValue.True;
                                            } else if (value === 'false' || value === '0') {
                                                docValue = LogicalValue.False;
                                            }
                                        }
                                        break;
                                    default:
                                        if (value.toString) {
                                            docValue = value.toString();
                                        }
                                        break;
                                }
                                doc[`c${mapField.id}`] = docValue;
                            }
                        }
                        docs.push(doc);
                    }
                    log(`pushing "${mapTable.table}" to FrameLink database...`);
                    await Api.insertDocuments(mapTable.id, docs);
                }
            }
        } else {
            log(`cannot retrieve data from server`);
        }
        log(`sync data finished`);
        return response ? response.data : [];
    }

    /* Private Methods */

    private async createRelation(connection: IDataConnection, relation: IConnectionRelation,logCallback?: (msg: string) => void): Promise<IField | null> {
        const log = (msg: string) => logCallback ? logCallback(msg) : null;
        const from: IField | null = Field.get(relation.id);
        if (from && from.uiType !== FieldType.linked) {
            const toEntity: IEntity | null = this.getEntityByDbName(connection, relation.foreignTable);
            if (toEntity) {
                log(`creating relation from "${relation.table}.${relation.field}" to "${relation.foreignTable}.${relation.foreignField}"`);
                const toField: IField | undefined = toEntity.fields.find(f => {
                    return f.connection && f.connection.field === relation.foreignField;
                });
                if (toField) {
                    if (from.systemType === FieldSystemType.number) {
                        await FieldPromote.toBasic(from.id, FieldType.text);
                    }
                    if (toField.systemType === FieldSystemType.number) {
                        await FieldPromote.toBasic(toField.id, FieldType.text);
                    }
                    try {
                        /* creating linked field */
                        const linkedField: IField = await FieldPromote.toLinked(from.id, {
                            entityId: toEntity.id,
                            fieldId: toField.id
                        });
                        /* set new field connection data */
                        if (from.connection) {
                            from.connection.id = linkedField.id;
                            await Field.setConnection(linkedField.id, from.connection);
                        }
                        // /* updating linked template to display in table */
                        // const textField: IField | undefined = toEntity.fields.find(f => f.systemType === FieldSystemType.string && f.id !== toField.id);
                        // if (textField) {
                        //     await FieldPromote.toLinked(linkedField.id, {
                        //         entityId: toEntity.id,
                        //         fieldId: textField.id
                        //     });
                        // }
                        const mapEntity: IConnectionEntity | undefined = connection.mapping.find(ce => ce.id === linkedField.entityId);
                        if (mapEntity) {
                            const mapField: IConnectionField | undefined = mapEntity.fields.find(ce => ce.id === from.id);
                            if (mapField) {
                                mapField.id = linkedField.id;
                                log(`mapping changed for "${relation.name}"`);
                            } else {
                                log(`error: cannot change mapping for "${relation.name}"`);
                            }
                        } else {
                            log(`error: cannot change mapping for "${relation.name}"`);
                        }
                        log(`relation "${relation.name}" successfully created`);
                        return linkedField;
                    } catch (e) {
                        log(`error: failed to create relation "${relation.name}": error changing type to linked field`);
                    }
                }
            }
        }
        log(`error: failed to create relation "${relation.name}"`);
        return null;
    }

    private getEntityByDbName(connection: IDataConnection, table: string): IEntity | null {
        const map: IConnectionEntity | undefined = connection.mapping.find(m => m.table === table);
        if (map) {
            return Entity.get(map.id);
        }
        return null;
    }

    private getUid(name: string): string {
        // todo - proper uuid
        return `${Date.now()}`
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IDataConnection> {
        return Parameter.get(ParamStorage.DataConnections);
    }

}

const DataConnection = new DataConnectionSingleton();

export default DataConnection;